/**
 * Main Chatbot Component
 * Refactored to use smaller, reusable components
 */

import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { ScrollArea } from './ui/scroll-area';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

// Import chatbot components
import {
  ChatHeader,
  ChatHistory,
  ChatInput,
  MessageList,
  TopicSelector,
  HISTORY_STORAGE_KEY,
  MAX_HISTORY_ITEMS,
  MAX_FILE_SIZE,
  MAX_IMAGE_SIZE,
  BACKEND_URL,
  contextualQuestions,
  sectionNames,
  getSectionFromLocation,
  getQuestionsForTopic,
  getContextualSuggestions,
  topicHierarchy
} from './chatbot';

export const Chatbot = () => {
  const { language, t } = useLanguage();
  const location = useLocation();
  
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [sessionId, setSessionId] = useState(() => `session-${Date.now()}`);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [currentSection, setCurrentSection] = useState(() => getSectionFromLocation(window.location));
  const [showTopicSelector, setShowTopicSelector] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [topicSelectorKey, setTopicSelectorKey] = useState(0); // Key to force TopicSelector reset
  const [selectedArea, setSelectedArea] = useState(null); // Track selected area (area-operacoes, area-financas)
  const [selectedSection, setSelectedSection] = useState(null); // Track selected section (e.g., execucao-operacional-2026)
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // Load conversation history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (savedHistory) {
      try {
        setConversationHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error('Error loading chat history:', e);
      }
    }
  }, []);

  // Save current conversation to history
  const saveToHistory = () => {
    if (messages.length < 2) return;
    
    const firstUserMessage = messages.find(m => m.type === 'user');
    const topic = currentSection !== 'default' ? currentSection : 'general';
    
    const newEntry = {
      id: sessionId,
      timestamp: Date.now(),
      topic: topic,
      preview: firstUserMessage?.content?.substring(0, 80) || 'Conversa',
      messageCount: messages.length,
      messages: messages,
      sessionId: sessionId
    };
    
    const existingIndex = conversationHistory.findIndex(h => h.id === sessionId);
    let updatedHistory;
    
    if (existingIndex >= 0) {
      updatedHistory = [...conversationHistory];
      updatedHistory[existingIndex] = newEntry;
    } else {
      updatedHistory = [newEntry, ...conversationHistory].slice(0, MAX_HISTORY_ITEMS);
    }
    
    setConversationHistory(updatedHistory);
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(updatedHistory));
  };

  // Load conversation from history
  const loadFromHistory = (historyItem) => {
    setMessages(historyItem.messages);
    setSessionId(historyItem.id);
    setCurrentSection(historyItem.topic || 'default');
    setShowSuggestions(false);
    setShowHistory(false);
    setShowTopicSelector(false);
  };

  // Delete conversation from history
  const deleteFromHistory = (historyId) => {
    const updatedHistory = conversationHistory.filter(h => h.id !== historyId);
    setConversationHistory(updatedHistory);
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(updatedHistory));
  };

  // Clear all history
  const clearAllHistory = () => {
    if (window.confirm(language === 'pt' ? 'Apagar todo o histórico?' : 'Clear all history?')) {
      setConversationHistory([]);
      localStorage.removeItem(HISTORY_STORAGE_KEY);
    }
  };

  // Handle close
  const handleClose = () => {
    saveToHistory();
    setIsOpen(false);
    setShowHistory(false);
  };

  // Handle new session
  const handleNewSession = () => {
    saveToHistory();
    localStorage.removeItem('abc_main_chatbot_session');
    setMessages([]);
    setSessionId(`session-${Date.now()}`);
    setShowSuggestions(true);
    setShowResumeDialog(false);
    setShowHistory(false);
  };

  // Detect current section from URL
  useEffect(() => {
    const section = getSectionFromLocation(location);
    setCurrentSection(section);
    setShowTopicSelector(true);
  }, [location.search]);

  // Update section when chatbot opens
  useEffect(() => {
    if (isOpen) {
      const section = getSectionFromLocation(window.location);
      setCurrentSection(section);
      setShowTopicSelector(true);
    }
  }, [isOpen]);

  // Auto-close when navigating
  useEffect(() => {
    if (isOpen) {
      setIsOpen(false);
    }
  }, [location.pathname]);

  // Focus input when chatbot opens
  useEffect(() => {
    if (isOpen && inputRef.current && !showHistory && !showTopicSelector) {
      inputRef.current.focus();
    }
  }, [isOpen, showHistory, showTopicSelector]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Check for existing session on mount
  useEffect(() => {
    const savedSession = localStorage.getItem('abc_main_chatbot_session');
    if (savedSession) {
      try {
        const { sessionId: savedId, messages: savedMessages } = JSON.parse(savedSession);
        if (savedMessages && savedMessages.length > 0) {
          setSessionId(savedId);
          setMessages(savedMessages);
          setShowSuggestions(false);
          setShowResumeDialog(true);
        }
      } catch (e) {
        localStorage.removeItem('abc_main_chatbot_session');
      }
    }
  }, []);

  // Save session to localStorage when messages change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('abc_main_chatbot_session', JSON.stringify({
        sessionId,
        messages
      }));
    }
  }, [messages, sessionId]);

  // Handle sending messages
  const handleSendMessage = async (message) => {
    if (!message.trim()) return;

    setInputValue('');
    setShowSuggestions(false);
    setShowTopicSelector(false);

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat`, {
        message,
        session_id: sessionId,
        language
      });

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: response.data.response,
        sources: response.data.sources || [],
        awaitingInfoRequest: response.data.awaiting_info_request || false,
        awaitingEmail: response.data.awaiting_email || false,
        noDataAvailable: response.data.no_data_available || false,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: language === 'pt' 
          ? 'Desculpe, ocorreu um erro. Por favor, tente novamente.'
          : 'Sorry, an error occurred. Please try again.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle topic selection
  // Determine which area (Operations/Finance) a topic belongs to
  const getAreaFromTopic = (topicId) => {
    // Check if topicId is directly an area
    if (topicId === 'area-operacoes' || topicId === 'area-financas') {
      return topicId;
    }
    
    // Search through hierarchy to find parent area
    for (const area of topicHierarchy) {
      if (area.id === topicId) {
        return area.id;
      }
      
      // Search in children
      const searchChildren = (children, areaId) => {
        for (const child of children || []) {
          if (child.id === topicId) {
            return areaId;
          }
          if (child.children) {
            const found = searchChildren(child.children, areaId);
            if (found) return found;
          }
        }
        return null;
      };
      
      const found = searchChildren(area.children, area.id);
      if (found) return found;
    }
    
    return null;
  };

  const handleSelectTopic = (topicId) => {
    if (topicId === 'custom') {
      setShowTopicSelector(false);
      inputRef.current?.focus();
    } else {
      setCurrentSection(topicId);
      setShowTopicSelector(false);
      
      // Determine and save the area for this topic
      const area = getAreaFromTopic(topicId);
      if (area) {
        setSelectedArea(area);
      }
      
      // Save the selected section for filtering follow-up suggestions
      // This helps filter by period (2026 vs 2025)
      setSelectedSection(topicId);
    }
  };

  // Handle reset to main menu (level 1) - called when user clicks "Suficiente"
  const handleResetToMain = () => {
    // Clear messages to start fresh
    setMessages([]);
    // Show the topic selector at level 1
    setShowTopicSelector(true);
    setShowSuggestions(true);
    // Reset current section to default
    setCurrentSection(null);
    // Reset selected area
    setSelectedArea(null);
    // Reset selected section
    setSelectedSection(null);
    // Increment key to force TopicSelector to reset its internal navigation state
    setTopicSelectorKey(prev => prev + 1);
    // Clear local storage for this session to start fresh
    localStorage.removeItem('abc_main_chatbot_session');
    // Generate new session ID
    setSessionId(`session_${Date.now()}`);
  };

  // Handle back to current section - shows questions again without resetting to main
  // Called when user clicks "Explorar outros temas" after a response
  const handleBackToSection = () => {
    // Clear messages but keep the current section context
    setMessages([]);
    // Hide the topic selector to show the contextual questions instead
    // The condition for showing questions is: showSuggestions && !showTopicSelector && messages.length === 0
    setShowTopicSelector(false);
    setShowSuggestions(true);
    // Clear local storage for this session
    localStorage.removeItem('abc_main_chatbot_session');
    // Generate new session ID for fresh conversation
    setSessionId(`session_${Date.now()}`);
    // Note: We keep currentSection, selectedArea, and selectedSection intact
    // so the questions shown are for the same section (e.g., Mina e Acesso)
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      alert(language === 'pt' ? 'Ficheiro muito grande. Máximo: 10MB' : 'File too large. Maximum: 10MB');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_id', sessionId);

      const response = await axios.post(`${BACKEND_URL}/api/chat/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setUploadedFiles(prev => [...prev, {
          id: response.data.document_id,
          filename: response.data.filename,
          size: response.data.size
        }]);

        const uploadMessage = {
          id: Date.now(),
          type: 'system',
          content: language === 'pt'
            ? `📎 Documento "${file.name}" carregado. Pode agora fazer perguntas sobre o seu conteúdo.`
            : `📎 Document "${file.name}" uploaded. You can now ask questions about its content.`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, uploadMessage]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(language === 'pt' ? 'Erro ao carregar ficheiro.' : 'Error uploading file.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      alert(language === 'pt' ? 'Imagem muito grande. Máximo: 5MB' : 'Image too large. Maximum: 5MB');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_id', sessionId);

      const response = await axios.post(`${BACKEND_URL}/api/chat/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setUploadedImages(prev => [...prev, {
          id: response.data.image_id,
          filename: response.data.filename,
          size: response.data.size
        }]);

        const uploadMessage = {
          id: Date.now(),
          type: 'system',
          content: language === 'pt'
            ? `🖼️ Imagem "${file.name}" carregada. Pode pedir ao assistente para analisar.`
            : `🖼️ Image "${file.name}" uploaded. You can ask the assistant to analyze it.`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, uploadMessage]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Image upload error:', error);
      alert(language === 'pt' ? 'Erro ao carregar imagem.' : 'Error uploading image.');
    } finally {
      setIsUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  // Remove uploaded file
  const removeUploadedFile = async (fileId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/chat/documents/${sessionId}/${fileId}`);
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (error) {
      console.error('Error removing file:', error);
    }
  };

  // Remove uploaded image
  const removeUploadedImage = async (imageId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/chat/images/${sessionId}/${imageId}`);
      setUploadedImages(prev => prev.filter(img => img.id !== imageId));
    } catch (error) {
      console.error('Error removing image:', error);
    }
  };

  return (
    <>
      {/* Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-primary text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center z-50"
          data-testid="chatbot-toggle-btn"
        >
          <MessageSquare className="w-6 h-6" />
        </button>
      )}

      {/* Chatbot Window - Only render when open */}
      {isOpen && (
        <div 
          className="fixed bottom-6 right-6 w-[400px] h-[600px] bg-white rounded-xl shadow-2xl flex flex-col z-[9999] transition-all duration-300 opacity-100 scale-100"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
        
        {/* Header */}
        <ChatHeader
          language={language}
          showHistory={showHistory}
          setShowHistory={setShowHistory}
          onClose={handleClose}
        />

        {/* Resume Dialog */}
        {showResumeDialog && !showHistory && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-sm z-10 flex items-center justify-center p-6">
            <div className="text-center">
              <p className="text-slate-700 mb-4">
                {language === 'pt' 
                  ? 'Tem uma conversa anterior. Deseja continuar?'
                  : 'You have a previous conversation. Continue?'}
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setShowResumeDialog(false)}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm"
                >
                  {language === 'pt' ? 'Sim, continuar' : 'Yes, continue'}
                </button>
                <button
                  onClick={handleNewSession}
                  className="px-4 py-2 border border-slate-200 text-slate-600 rounded-lg text-sm"
                >
                  {language === 'pt' ? 'Nova conversa' : 'New conversation'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* History View */}
        {showHistory && (
          <ChatHistory
            language={language}
            conversationHistory={conversationHistory}
            onLoadHistory={loadFromHistory}
            onDeleteHistory={deleteFromHistory}
            onClearAllHistory={clearAllHistory}
            onNewSession={handleNewSession}
          />
        )}

        {/* Messages Area */}
        {!showHistory && (
          <ScrollArea className="flex-1 p-4">
            {/* Welcome Message - only when no messages */}
            {messages.length === 0 && !showTopicSelector && (
              <div className="mb-6">
                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {language === 'pt' 
                      ? 'Olá! Sou o Assistente de Gestão da ABC. Respondo com base em fontes internas aprovadas: ERP, orçamento, relatórios mensais e KPIs do dashboard. Todas as respostas incluem a origem dos dados.'
                      : 'Hello! I\'m the ABC Management Assistant. I respond based on approved internal sources: ERP, budget, monthly reports and dashboard KPIs. All responses include data source citations.'}
                  </p>
                  <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded">
                      {language === 'pt' ? '✓ Dados verificados' : '✓ Verified data'}
                    </span>
                    <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-600 px-2 py-0.5 rounded">
                      {language === 'pt' ? '✓ Fontes citadas' : '✓ Sources cited'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Topic Selector - can appear at any time when showTopicSelector is true */}
            {showTopicSelector && (
              <div className="mb-6">
                {messages.length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-3 mb-4">
                    <p className="text-xs text-slate-500">
                      {language === 'pt' 
                        ? 'Seleccione um novo tema para explorar:' 
                        : 'Select a new topic to explore:'}
                    </p>
                  </div>
                )}
                <TopicSelector
                  key={topicSelectorKey}
                  language={language}
                  onSelectTopic={handleSelectTopic}
                  onSendMessage={handleSendMessage}
                  initialSection={selectedSection}
                />
                {messages.length > 0 && (
                  <button
                    onClick={() => setShowTopicSelector(false)}
                    className="mt-3 text-xs text-slate-500 hover:text-primary flex items-center gap-1"
                  >
                    <span>←</span>
                    <span>{language === 'pt' ? 'Voltar à conversa' : 'Back to conversation'}</span>
                  </button>
                )}
              </div>
            )}

            {/* Contextual Questions (after topic selected, no messages yet) */}
            {showSuggestions && !showTopicSelector && messages.length === 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                    {language === 'pt' ? `Perguntas sobre ${sectionNames[currentSection]?.[language] || 'Geral'}` : `Questions about ${sectionNames[currentSection]?.[language] || 'General'}`}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  {(getQuestionsForTopic(currentSection)?.[language] || contextualQuestions.default[language]).map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSendMessage(question)}
                      className="text-left text-sm text-primary bg-slate-50 hover:bg-slate-100 rounded-lg px-4 py-3 transition-colors border border-slate-200"
                      data-testid={`suggested-question-${idx}`}
                    >
                      {question}
                    </button>
                  ))}
                </div>

                {/* Back to topics */}
                <button
                  onClick={() => {
                    setShowTopicSelector(true);
                    setTopicSelectorKey(prev => prev + 1);
                  }}
                  className="mt-4 w-full py-2.5 px-4 bg-amber-50 hover:bg-amber-100 text-amber-700 font-medium text-sm rounded-lg border border-amber-200 transition-all duration-200 flex items-center justify-center gap-2"
                  data-testid="back-to-topics-questions-btn"
                >
                  <span className="text-base">←</span>
                  <span>{language === 'pt' ? 'Voltar aos temas' : 'Back to topics'}</span>
                </button>
              </div>
            )}

            {/* Message List - only when not showing topic selector */}
            {!showTopicSelector && (
              <MessageList
                messages={messages}
                isLoading={isLoading}
                onSendMessage={handleSendMessage}
                onResetToMain={handleResetToMain}
                language={language}
              />
            )}

            {/* Back to topics button - when there are messages and not showing topic selector */}
            {messages.length > 0 && !isLoading && !showTopicSelector && (
              <div className="mt-4 pt-3 border-t border-slate-100">
                <button
                  onClick={handleBackToSection}
                  className="w-full py-2.5 px-4 bg-amber-50 hover:bg-amber-100 text-amber-700 font-medium text-sm rounded-lg border border-amber-200 transition-all duration-200 flex items-center justify-center gap-2"
                  data-testid="back-to-topics-btn"
                >
                  <span className="text-base">←</span>
                  <span>{language === 'pt' ? 'Explorar outros temas' : 'Explore other topics'}</span>
                </button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </ScrollArea>
        )}

        {/* Input Area */}
        {!showHistory && (
          <ChatInput
            language={language}
            inputValue={inputValue}
            setInputValue={setInputValue}
            isLoading={isLoading}
            isUploading={isUploading}
            uploadedFiles={uploadedFiles}
            uploadedImages={uploadedImages}
            onSendMessage={handleSendMessage}
            onClose={handleClose}
            onFileUpload={handleFileUpload}
            onImageUpload={handleImageUpload}
            onRemoveFile={removeUploadedFile}
            onRemoveImage={removeUploadedImage}
            fileInputRef={fileInputRef}
            imageInputRef={imageInputRef}
            inputRef={inputRef}
            messages={messages}
            t={t}
          />
        )}
      </div>
      )}
    </>
  );
};

export default Chatbot;
